import React, { useEffect } from 'react';
import { CheckoutFormValues, CheckoutFormPayload } from '../CheckoutForm/CheckoutForm';
import CheckoutFormModal from '../CheckoutForm/CheckoutFormModal';
import { useProducts, CartItem, InkindPicker, CheckoutFormInitiatedOnType } from '@gik/checkout';
import { InkindPageViewHistoryItem } from '@gik/inkind-page';
import { useApi } from '@gik/core/api';
import ExternalProductInterstitial from '../ExternalProductInterstitial/ExternalProductInterstitial';
import { placeOrder } from '@gik/checkout/api';
import { Analytics } from '@gik/core/utils';
import { openNonD2CUrl, alertNoNonD2CUrlAvailable } from '@gik/checkout/utils/productUtils';
import i18n from '@gik/i18n';
import { IAnalyticsProps } from '@gik/core/utils/Analytics';
import { useUserStore } from '@gik/core/store/UserStore';
import { useUser } from '@gik/api/users/user';
import { useInkind } from '@gik/api/inkinds/inkind';
import { Product } from '@gik/core/models/gik/Product';
import isInkindPageRecipientInfoComplete from '@gik/inkind-page/utils/isInkindPageRecipientInfoComplete';

interface CheckoutLauncherProps {
  initiatedOn: CheckoutFormInitiatedOnType;
  initialInkindRouteId?: string;
  skipProductsCheckoutStep?: boolean;
  hideProductsCheckoutStep?: boolean;
  navigateToInkindPageAfterPurchase?: boolean;
  onSuccessfulPurchase?(): void;
  onCloseSuccessfulPurchase?(): void;
  onCloseCheckout?(): void;
  cart?: CartItem[];
  cachedProducts?: Product[]; // used to avoid requesting products again on the wishlist
  inkindPageViewHistory?: InkindPageViewHistoryItem[];
  // TODO: add loading state
  child(onClick: (ev: React.MouseEvent<HTMLElement>) => void, isLoading: boolean): React.ReactElement;
}

export default function CheckoutLauncher({
  initiatedOn,
  initialInkindRouteId,
  skipProductsCheckoutStep,
  hideProductsCheckoutStep,
  cart,
  inkindPageViewHistory,
  cachedProducts,
  navigateToInkindPageAfterPurchase,
  onSuccessfulPurchase,
  onCloseSuccessfulPurchase,
  onCloseCheckout,
  child,
}: CheckoutLauncherProps): React.ReactElement {
  const userStore = useUserStore();
  const userId = userStore.id;
  const { data: user } = useUser(userId);
  const { data: uncachedProducts } = useProducts(
    cachedProducts ? null : cart?.length ? cart?.map(item => item.productId) : null
  );

  const products = cachedProducts || uncachedProducts;

  const [buyForSomeoneElse, setBuyForSomeoneElse] = React.useState(false);
  const [checkoutFormOpen, setCheckoutFormModalOpen] = React.useState(false);
  const [chooseInkindPopoverOpen, setChooseInkindPopoverOpen] = React.useState(false);
  const [externalProductInterstitialOpen, setExternalProductInterstitialOpen] = React.useState(false);
  const [selectedInkindRouteId, setSelectedInkindRouteId] = React.useState(initialInkindRouteId);
  const { data: selectedInkind, mutate: reloadSelectedInkind } = useInkind(selectedInkindRouteId);
  const [checkoutErrorResponse, setCheckoutErrorResponse] = React.useState(null);
  const [paymentConfirmationValues, setPaymentConfirmationValues] = React.useState(null);

  const isLoading = !products?.length || (userId && !user);

  const productNonD2CUrl = products?.[0].productNonD2CUrl;
  const disableInterstitial = products?.[0].disableInterstitial;

  const hasNonD2CProduct = products?.some(product => product.isNonD2C);
  const hasGiftbox = products?.some(product => product.checkoutType === 'giftbox');
  const hasGikPremium = products?.some(product => product.checkoutType === 'gik-premium');
  const hasVirtualProduct = products?.some(product => product.checkoutType === 'virtual-product'); // GIK virtual product with vendor
  const hasShipping = hasGiftbox || hasVirtualProduct;
  const hasIntegratedCheckout = hasNonD2CProduct || hasShipping || hasGikPremium;

  const [initialData, setInitialData] = React.useState<CheckoutFormValues>(null);

  const { data: wcStatus } = useApi<boolean>(`orders/wc-status`);
  // if wcStatus is off (killswitch), then the interstitial should always be used
  let forceInterstitial: boolean;

  if (wcStatus === false) {
    if (!user || !user.emailAddress) {
      forceInterstitial = true;
    } else {
      if (!user.emailAddress.endsWith('giveinkind.com') && !user.emailAddress.endsWith('codiv.tech')) {
        forceInterstitial = true;
      } else {
        forceInterstitial = false;
      }
    }
  }

  function getAnalyticsProps(props: IAnalyticsProps = {}): IAnalyticsProps {
    return {
      productNames: products?.map(item => item.name).join(','),
      productIds: products?.map(item => item.id).join(','),
      productCheckoutTypes: products?.map(item => item.checkoutType).join(','),
      inkindRouteId: selectedInkindRouteId,
      userId, // id of the purchaser
      initiatedOn,
      ...props,
    };
  }

  useEffect(() => {
    // FIXME: when creating an inkind page, should specify recipient first and last name.
    // from Rigo:
    // - For gift boxes that's ok. All that means is that the middle name and last name will potentially be together in the last name field.
    // - Not an issue that can easily be solved as a lot of people put "The Smith Family" as the recipient name
    const recipientNameParts = (selectedInkind?.recipientFullName || '').split(' ');
    const recipientFirstName = recipientNameParts.length > 0 ? recipientNameParts[0] : '';
    const recipientLastName = recipientNameParts.length > 1 ? recipientNameParts.slice(1).join(' ') : '';

    setInitialData({
      billing: {
        country: 'US',
        firstName: user?.firstName,
        lastName: user?.lastName,
        state: user?.state,
        email: user?.emailAddress,
      },
      shipping: {
        country: 'US',
        email: selectedInkind?.recipientEmail,
        firstName: recipientFirstName,
        lastName: recipientLastName,
        address1: selectedInkind?.address,
        address2: selectedInkind?.address2,
        city: selectedInkind?.city,
        postalCode: selectedInkind?.zip,
        state: selectedInkind?.stateCode,
      },
      summary: { agree: false, subscribeToNewsletter: true },
      cart: cart,
    } as CheckoutFormValues);
  }, [cart, selectedInkind, user]);

  function openExternalProduct() {
    if (disableInterstitial) {
      const productNonD2CUrl = products?.length ? products[0].productNonD2CUrl : null;
      if (productNonD2CUrl) {
        openNonD2CUrl(productNonD2CUrl);
      } else {
        alertNoNonD2CUrlAvailable();
      }
    } else {
      setExternalProductInterstitialOpen(true);
    }
  }

  function onClick() {
    if (!hasIntegratedCheckout && (!inkindPageViewHistory || !inkindPageViewHistory.length || initialInkindRouteId)) {
      openExternalProduct();
    } else if (initialInkindRouteId && hasIntegratedCheckout) {
      if (forceInterstitial) {
        handleForceInterstitial();
      } else {
        // buying for a page - launch checkout modal
        setCheckoutFormModalOpen(true);
      }
    } else {
      setChooseInkindPopoverOpen(true);
    }
    Analytics.fireEvent('BuyClick', getAnalyticsProps());
  }

  async function handleSubmit(values: CheckoutFormPayload, updateCheckoutStatusText: (text: string) => void) {
    try {
      const response = await placeOrder(values, updateCheckoutStatusText);
      setPaymentConfirmationValues(response);

      Analytics.fireEvent(
        'CheckoutCompletePurchase',
        getAnalyticsProps({
          total: response.total.toString(),
          tip: response.tip.toString(),
          orderID: response.orderId.toString(),
          shippingOptionName: values.shipping?.shippingOptionName,
        })
      );
      onSuccessfulPurchase?.();
      return true;
    } catch (err) {
      console.error(err);
      const errorMessage = err.message || i18n.t('checkout.genericBackendError');
      setCheckoutErrorResponse(errorMessage);

      return false;
    }
  }

  function handleOnCloseCheckout() {
    // close the modal
    setCheckoutFormModalOpen(false);
    setCheckoutErrorResponse(null);
    onCloseCheckout?.();
    // reset form values if payment was completed
    if (paymentConfirmationValues) {
      //alert('Checkout launcher successful purchase');
      onCloseSuccessfulPurchase?.();
      setPaymentConfirmationValues(null);
      setInitialData(initialData);
    }
  }

  function handleForceInterstitial() {
    if (!hasIntegratedCheckout) {
      openExternalProduct();
    } else {
      openInterstitial(true);
    }
  }

  function onInkindPageChosen(inkindRouteId: string) {
    setChooseInkindPopoverOpen(false);
    setSelectedInkindRouteId(inkindRouteId);
    reloadSelectedInkind();

    Analytics.fireEvent('BuySelectPage', getAnalyticsProps());

    if (forceInterstitial) {
      handleForceInterstitial();
      return;
    }

    if (!hasIntegratedCheckout) {
      openExternalProduct();
    } else {
      setCheckoutFormModalOpen(true);
    }
  }

  function openInterstitial(withInfo = false) {
    setChooseInkindPopoverOpen(false);
    if (withInfo === false) setSelectedInkindRouteId(null);
    setExternalProductInterstitialOpen(true);
    Analytics.fireEvent('BuySelectPage', getAnalyticsProps());
  }

  function onBuyForSomeoneElse() {
    Analytics.fireEvent('BuyForSomeoneElse', getAnalyticsProps());
    setChooseInkindPopoverOpen(false);
    if (hasGiftbox || hasVirtualProduct) {
      setBuyForSomeoneElse(true);
      setCheckoutFormModalOpen(true);
    } else if (productNonD2CUrl) {
      if (disableInterstitial) {
        openNonD2CUrl(productNonD2CUrl);
      } else {
        openInterstitial();
      }
    } else {
      alertNoNonD2CUrlAvailable();
    }
  }

  // TODO: refactor this to launch ExternalProductInterstitial through UI (same as dialogs and alerts)
  return (
    <ExternalProductInterstitial
      isOpen={externalProductInterstitialOpen}
      sourceName={((products || [])[0]?.sources || [])[0]?.name || 'Unknown'}
      externalLink={productNonD2CUrl}
      products={products}
      inkindRouteId={selectedInkindRouteId}
      initiatedOn={initiatedOn}
      recipientEmail={!hasGiftbox && selectedInkind ? selectedInkind.recipientEmail : undefined}
      shippingAddressLines={
        selectedInkind
          ? [
              selectedInkind.recipientFullName,
              selectedInkind.address,
              selectedInkind.address2,
              `${selectedInkind.location} ${selectedInkind.zip}`,
            ]
          : undefined
      }
      onClose={() => setExternalProductInterstitialOpen(false)}
    >
      {!isLoading && (selectedInkind || buyForSomeoneElse) && (
        <CheckoutFormModal
          initialData={initialData}
          skipProducts={skipProductsCheckoutStep}
          hideProducts={hideProductsCheckoutStep}
          showModal={checkoutFormOpen}
          navigateToInkindPageAfterPurchase={navigateToInkindPageAfterPurchase}
          onClose={handleOnCloseCheckout}
          onSubmit={handleSubmit}
          errorMessage={checkoutErrorResponse}
          shippingEditable={Boolean(
            hasShipping && (!selectedInkind || !isInkindPageRecipientInfoComplete(selectedInkind))
          )}
          shipping={hasShipping}
          hasShipping={hasGiftbox}
          paymentConfirmationValues={paymentConfirmationValues}
          initiatedOn={initiatedOn}
          inkindRouteId={selectedInkind?.routeId}
        />
      )}
      <InkindPicker
        buyForSomeoneElseEnabled={true}
        popoverTrigger={child(onClick, isLoading)}
        inkindPageViewHistory={inkindPageViewHistory}
        isOpen={chooseInkindPopoverOpen}
        productNonD2CUrl={productNonD2CUrl}
        disableInterstitial={disableInterstitial}
        onClose={() => setChooseInkindPopoverOpen(false)}
        onBuyForSomeoneElse={onBuyForSomeoneElse}
        onInkindPageChosen={onInkindPageChosen}
      />
    </ExternalProductInterstitial>
  );
}
